/* eslint-disable react/no-danger */
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import ReCAPTCHA from 'react-google-recaptcha';

import withStyles from 'isomorphic-style-loader/withStyles';
import ReactModal from 'react-modal';
import cx from 'classnames';
import s from './Redeem.scss';
import Label from '../Label';
import TextInput from '../TextInput';
import Button from '../Clickable';
import Loading from '../Loading';
import Brand from '../Brand';
import CountrySwitcher from '../CountrySwitcher/CountrySwitcher';
import {
  addRedeem,
  createBasket,
  getBasket,
  updateBasketItem,
} from '../../actions/basket';
import {
  setSalesChannel,
  redirectToLogin,
  redirectTo,
  getCountryPresentations,
} from '../../actions/config';
import { setCountry, setPath } from '../../actions/intl';
import { formMessages, redeemMessages as messages } from '../../defineMessages';
import {
  SHOP_PATH,
  ICONS,
  ACCOUNT_TYPE,
  SALES_CHANNELS,
  SALES_CHANNELS_GROUP,
  autoCompleteValues,
} from '../../constants';
import Icon from '../Icon/Icon';
import {
  flattenBasketData,
  trimWhiteSpace,
  isVoucherCodePorOrSgk,
  removeExtraHyphens,
} from '../../helpers';
import { getSalesChannelBasketId } from '../../helpers/userStorage';
import getBasketQuery from '../../actions/queries/getBasket.graphql';
import ApplicationContext from '../ApplicationContext';

class Redeem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      voucher: '',
      pin: '',
      loading: false,
      hideChangeCountryModal: true,
      errors: {},
    };
    this.submitVoucher = this.submitVoucher.bind(this);
    this.handleRecaptchaVerify = this.handleRecaptchaVerify.bind(this);
    this.handleRecaptchaLoad = this.handleRecaptchaLoad.bind(this);
  }

  componentDidMount() {
    if (this.captcha) {
      this.captcha.reset();
    }
    const { voucherCode, basket } = this.props;
    if (voucherCode) {
      this.setState({
        voucher: voucherCode,
      });
    }
    if (basket && !basket.id) {
      this.setState({
        hideChangeCountryModal: false,
      });
    }
  }

  setFieldError({ name, value }) {
    const { errors } = this.state;
    this.setState({
      errors: {
        ...errors,
        [name]: value,
      },
    });
  }

  setField({ name, value }) {
    this.setState({
      [name]: value,
    });
  }

  handleRecaptchaLoad() {
    if (this.captcha) {
      this.captcha.reset();
    }
  }

  // the state is not keeping up with the updates. Checking and setting correct basket.
  // this should be a temporary solution
  async ensureCorrectBasketAndSalesChannel() {
    const { dispatch, countryCode } = this.props;
    await dispatch(setSalesChannel(SALES_CHANNELS.REDEEM));
    // check userStorage for available baskets
    const { basketId: existingBasketId } =
      getSalesChannelBasketId({
        salesChannelId: SALES_CHANNELS.REDEEM,
        countryCode,
      }) || {};

    if (existingBasketId) {
      await dispatch(getBasket(existingBasketId));
    } else {
      await dispatch(createBasket(SALES_CHANNELS.REDEEM));
    }
  }

  changeCountryCode(value) {
    const { dispatch } = this.props;
    dispatch(setCountry(value));
    dispatch(setPath({ country: value }));
  }

  handleRecaptchaVerify(token) {
    this.submitVoucher(token);
  }

  validation() {
    const { voucher, pin } = this.state;
    if (!voucher) {
      this.setFieldError({
        name: 'voucher',
        value: this.props.intl.formatMessage({
          ...formMessages.required,
        }),
      });
      return false;
    }
    const needsPin = !isVoucherCodePorOrSgk(voucher);
    if (needsPin && !pin) {
      this.setFieldError({
        name: 'pin',
        value: this.props.intl.formatMessage({
          ...formMessages.required,
        }),
      });
      return false;
    }
    if (!needsPin) {
      this.setState({ loading: true });
      if (this.captcha) {
        this.captcha.reset();
        this.captcha.execute();
      }
      return false;
    }
    return true;
  }

  async submitVoucher(token) {
    this.setState({ loading: true });

    if (!token) {
      const isValid = this.validation();
      if (!isValid) {
        this.setState({ loading: false });
        return false;
      }
    }

    const {
      intl: { locale },
      config: { salesChannels },
      dispatch,
      salesChannelId,
      basketSalesChannelId,
      salesChannelGroup,
      basket,
      countryCode,
      client,
    } = this.props;

    const { voucher: valueCode, pin: securityCode } = this.state;
    let voucher = {
      valueCode: removeExtraHyphens(valueCode),
      securityCode: token,
    };

    if (valueCode && !isVoucherCodePorOrSgk(valueCode) && securityCode) {
      voucher = {
        ...voucher,
        securityCode,
      };
    }

    async function getAndAddAllB2cDeliveriesToBasketIfNoVouchersInBasket() {
      // Ignore if there already is a voucher in basket, meaning the user is already using their redeem basket.
      if (basket.vouchers?.length) {
        return;
      }

      const basketIdObj = getSalesChannelBasketId({
        salesChannelId: SALES_CHANNELS.B2C,
        countryCode,
      });

      // Ignore if there is no B2C basket in storage.
      if (!basketIdObj) {
        return;
      }

      const {
        data: {
          baskets: { basketById },
        },
      } = await client.query({
        query: getBasketQuery,
        variables: {
          id: basketIdObj.basketId,
        },
        context: {
          service: 'graphqlHost',
          fetchPolicy: 'network-only',
        },
      });

      // Ignore if there are no products in the B2C basket.
      if (!basketById.deliveries.length) {
        return;
      }

      const addProducts = flattenBasketData(basketById.deliveries).map(
        ({
          stockKeepingUnit,
          productId,
          deliveryMethod,
          quantity,
          giftcardValue,
          recipientAddress,
          recipientEmail,
          recipientName,
          recipientPhone,
        }) => ({
          stockKeepingUnit,
          productId,
          deliveryMethod,
          quantity,
          giftcardValue,
          recipientAddress,
          recipientEmail,
          recipientName,
          recipientPhone,
          valueCurrency: basketById.paymentCurrency,
        }),
      );

      const { id } = await dispatch(
        updateBasketItem({
          addProducts,
          doClearProducts: true,
        }),
      );

      // We need to get the basket again in order to populate presentation data in the redux store.
      await dispatch(getBasket(id));
    }

    const portalWithWrongBasket = salesChannelId !== basketSalesChannelId;

    if (salesChannelGroup !== SALES_CHANNELS_GROUP.PORTAL) {
      await this.ensureCorrectBasketAndSalesChannel();
    } else if (portalWithWrongBasket) {
      // Safeguard against using a mainsite basket when in a portal
      await dispatch(createBasket(SALES_CHANNELS.REDEEM));
    }

    // Action shows any errors. Just skip redirect if any error.
    // Returns salesChannelId when voucher starts with 'por-' and is for another sales channel
    const res = await dispatch(addRedeem({ voucher, salesChannelGroup }));
    if (!res) {
      this.setState({ loading: false });
      return false;
    }
    if (typeof res === 'string') {
      const { id } = await dispatch(setSalesChannel(res));
      if (id) {
        // Use existing portal basket if there is one in storage, otherwise create one.
        const portalBasketIdObj = getSalesChannelBasketId({
          salesChannelId: id,
          countryCode,
        });
        if (portalBasketIdObj) {
          await dispatch(getBasket(portalBasketIdObj.basketId));
        } else {
          await dispatch(createBasket(id));
        }

        if (this.captcha) {
          this.captcha.reset();
          await this.captcha.execute();

          if (!salesChannels[id]) {
            this.setState({ loading: false });
            return false;
          }
          const portalPath = `/portal/${salesChannels[id]?.slug}`;

          await dispatch(redirectTo({ pathname: portalPath }));

          this.setState({ loading: false });
        }
      } else {
        console.error(`Sales Channel ${res} does not exist`);
        this.setState({ loading: false });
      }
      return false;
    }

    await getAndAddAllB2cDeliveriesToBasketIfNoVouchersInBasket();

    if (locale && countryCode) {
      await dispatch(
        getCountryPresentations({
          countryCode,
          locale,
        }),
      );
    }
    await dispatch(redirectTo({ pathname: SHOP_PATH }));

    return true;
  }

  static contextType = ApplicationContext;

  render() {
    const {
      intl: { formatMessage },
      authLoading,
      presentations,
      mode,
      dispatch,
      redirectToShop,
      backLink,
      redirecting,
      countryCode,
    } = this.props;
    const { loading } = this.state;
    const {
      config: { reCaptcha },
    } = this.context;
    if (redirecting) {
      return <Loading />;
    }

    if (mode === ACCOUNT_TYPE.B2B) {
      // Replace with effect hook
      dispatch(redirectToLogin());
      return <Loading />;
    }

    if (redirectToShop && mode === ACCOUNT_TYPE.REDEEM) {
      // Replace with effect hook
      dispatch(redirectTo({ pathname: SHOP_PATH }));
      return <Loading />;
    }
    // focuslayout
    return (
      <>
        <section>
          <section className={s.infoBox}>
            <Label tag="h1" bold>
              {presentations.pageHeader ||
                formatMessage({ ...messages.redeemTitle })}
            </Label>
            <p
              dangerouslySetInnerHTML={{
                __html:
                  presentations.pageBody ||
                  formatMessage({ ...messages.subtitle }),
              }}
            />
          </section>
          <form
            noValidate
            className={s.form}
            onSubmit={event => {
              event.preventDefault();
              this.submitVoucher();
              return false;
            }}
          >
            {backLink && (
              <Button
                className={s.back}
                onClick={evt => {
                  evt.preventDefault();
                  window.history.go(-1);
                }}
              >
                <Icon
                  name={ICONS.CHEVRON_LEFT}
                  className={s.back__icon}
                  fill="#ffffff"
                  size={12}
                />
                <FormattedMessage {...messages.backToHome} />
              </Button>
            )}
            <div className={s.form__body}>
              <TextInput
                name="voucher"
                label={formatMessage({ ...messages.voucherCode })}
                showLabel
                required
                autoComplete={autoCompleteValues.autoCompleteOff}
                hasError={this.state.errors.voucher}
                onFocus={() => this.setFieldError({ name: 'voucher' })}
                onChange={value => {
                  this.setField({
                    name: 'voucher',
                    value: trimWhiteSpace(value),
                  });
                }}
                value={this.state.voucher}
              />
              <TextInput
                name="pin"
                label={formatMessage({ ...messages.pinCode })}
                autoComplete={autoCompleteValues.autoCompleteOff}
                showLabel
                hasError={this.state.errors.pin}
                onFocus={() => this.setFieldError({ name: 'pin' })}
                onChange={value => {
                  this.setField({ name: 'pin', value: trimWhiteSpace(value) });
                }}
              />
              <div className={s.recaptcha}>
                <ReCAPTCHA
                  ref={el => {
                    this.captcha = el;
                  }}
                  size="invisible"
                  sitekey={reCaptcha}
                  onChange={this.handleRecaptchaVerify}
                  asyncScriptOnLoad={this.handleRecaptchaLoad}
                />
              </div>
            </div>
            <div className={s.form__actions}>
              <Button
                id="submitRedeem"
                fluid
                primary
                type="submit"
                disabled={authLoading || loading}
                label={formatMessage({ ...messages.register })}
              />
            </div>
          </form>
        </section>
        <ReactModal
          isOpen={!this.state.hideChangeCountryModal}
          contentLabel="changeCountryModal"
          className={{
            base: cx(s.root),
            afterOpen: s.afterOpen,
            beforeClose: s.beforeClose,
          }}
          overlayClassName={s.overlay}
          bodyOpenClassName={s.open}
          htmlOpenClassName={s.open}
          closeTimeoutMS={300}
        >
          <div className={s.changeCountryModal}>
            <Brand />
            <CountrySwitcher
              className={s.switcher}
              label={formatMessage({ ...messages.checkYourLocation })}
              basic
              value={countryCode}
              onChange={value => {
                this.changeCountryCode(value);
              }}
            />
            <Button
              id="confirmLocation"
              className={s.confirmButton}
              fluid
              primary
              onClick={evt => {
                evt.preventDefault();
                this.setField({
                  name: 'hideChangeCountryModal',
                  value: true,
                });
              }}
              disabled={authLoading || loading}
              label={formatMessage({ ...messages.checkLocationConfirm })}
            />
          </div>
        </ReactModal>
      </>
    );
  }
}

Redeem.propTypes = {
  intl: PropTypes.shape().isRequired,
  authLoading: PropTypes.bool.isRequired,
  presentations: PropTypes.shape(),
  mode: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  basketSalesChannelId: PropTypes.string,
  salesChannelId: PropTypes.string.isRequired,
  redirectToShop: PropTypes.bool,
  backLink: PropTypes.bool,
  redirecting: PropTypes.bool,
  salesChannelGroup: PropTypes.string.isRequired,
  voucherSalesChannelId: PropTypes.string,
  config: PropTypes.shape().isRequired,
  countryCode: PropTypes.string.isRequired,
  basket: PropTypes.shape({
    id: PropTypes.string,
    vouchers: PropTypes.shape([]),
    paymentCurrency: PropTypes.string,
  }).isRequired,
  client: PropTypes.shape({ query: PropTypes.func.isRequired }).isRequired,
  voucherCode: PropTypes.string,
};

Redeem.defaultProps = {
  presentations: {},
  basketSalesChannelId: undefined,
  redirectToShop: false,
  backLink: false,
  redirecting: false,
  voucherSalesChannelId: undefined,
  voucherCode: undefined,
};

const mapState = state => ({
  authLoading: state.loading.authLoading,
  redirecting: state.loading.redirecting,
  mode: state.user.mode,
  basketSalesChannelId: state.basket.salesChannelId,
  basket: state.basket,
  config: state.config,
  salesChannelId: state.config.salesChannelId,
  salesChannelGroup:
    state.config.salesChannels[state.config.salesChannelId].group,
  countryCode: state.intl.countryCode,
});

export default compose(injectIntl, withStyles(s), connect(mapState))(Redeem);
